import FolderOffOutlined from '@mui/icons-material/FolderOffOutlined'
import { Box, Pagination, Skeleton, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { FileRepositoryModel } from 'src/models/file-repository-model'
import { FileCard } from './FileCard'

interface FileRepositoryGridProps {
  entries: FileRepositoryModel[]
  loading: boolean
  total: number
  page: number
  limit: number
  directoryChildCounts: Record<string, number>
  onPageChange: (page: number) => void
  onDirectoryClick: (entry: FileRepositoryModel) => void
  onFileClick: (entry: FileRepositoryModel) => void
  onRenameClick: (entry: FileRepositoryModel) => void
  onMoveClick: (entry: FileRepositoryModel) => void
  onDeleteClick: (entry: FileRepositoryModel) => void
  onDownload: (entry: FileRepositoryModel) => void
  canUpdate: boolean
  canDelete: boolean
  canDownload: boolean
}

const gridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: 2,
}

export function FileRepositoryGrid({
  entries,
  loading,
  total,
  page,
  limit,
  directoryChildCounts,
  onPageChange,
  onDirectoryClick,
  onFileClick,
  onRenameClick,
  onMoveClick,
  onDeleteClick,
  onDownload,
  canUpdate,
  canDelete,
  canDownload,
}: FileRepositoryGridProps) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <Box sx={gridSx}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            width="100%"
            height={160}
            sx={{ borderRadius: '8px' }}
          />
        ))}
      </Box>
    )
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(0,0,0,0.5)' }}>
        <FolderOffOutlined
          sx={{ fontSize: 64, mb: 2, color: 'rgba(0,0,0,0.25)' }}
        />
        <Typography variant="h6">{t('fileRepository.empty.title')}</Typography>
        <Typography variant="body2">
          {t('fileRepository.empty.subtitle')}
        </Typography>
      </Box>
    )
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <>
      <Box sx={gridSx}>
        {entries.map((entry) => (
          <FileCard
            key={entry.id}
            entry={entry}
            childCount={
              entry.type === 'DIRECTORY'
                ? directoryChildCounts[entry.id]
                : undefined
            }
            onDirectoryClick={onDirectoryClick}
            onFileClick={onFileClick}
            onRenameClick={onRenameClick}
            onMoveClick={onMoveClick}
            onDeleteClick={onDeleteClick}
            onDownload={onDownload}
            canUpdate={canUpdate}
            canDelete={canDelete}
            canDownload={canDownload}
          />
        ))}
      </Box>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            shape="rounded"
            onChange={(_, p) => onPageChange(p)}
          />
        </Box>
      )}
    </>
  )
}
